import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import saveAs from 'file-saver';
import { Download, Eye, Trash2 } from 'lucide-react';

import ViewReportDialog from 'components/dialogs/ViewReportDialog';
import SocialShare from 'components/SocialShare';
import { useDeleteHealthReading } from 'services/face-scan/hooks';
import { ReadingDataWithSubParameters } from '../../../types';
import ReportActionButton from 'components/ReportActionButton';

interface ReportActionsProps {
  scanData?: ReadingDataWithSubParameters[];
  readingId: string;
  refetchScanReports?: () => void;
}

export default function ReportActions({
  scanData,
  readingId,
  refetchScanReports,
}: ReportActionsProps) {
  const { t } = useTranslation();

  const [openView, setOpenView] = useState<boolean>(false);

  const { mutate: onDelete, isPending: isDeleting } = useDeleteHealthReading({
    onSuccess: () => {
      refetchScanReports?.();
    },
  });

  const shareableReport = useCallback(() => {
    if (!scanData?.length) return '';

    let message = '';
    scanData?.forEach((item) => {
      const { category, key, value } = item;
      if (key === 'BLOOD_PRESSURE') {
        message += `${key}: ${item?.categories?.systolic?.value}/${item?.categories?.diastolic?.value} (${category})\n`;
        return;
      }
      message += `${key}: ${value} (${category})\n`;
    });
    return message;
  }, [scanData]);

  const scanReport = shareableReport();

  const onDownload = () => {
    const blob = new Blob([scanReport], { type: 'text/plain;charset=utf-8' });
    saveAs(blob, 'scan_report.txt');
  };

  return (
    <>
      <div className='flex items-center justify-center mt-2 mb-4 sm:mt-0 sm:justify-end gap-x-2'>
        <SocialShare content={shareableReport()} isLoading={false} />
        <ReportActionButton
          icon={Eye}
          text={t('view')}
          onClick={() => setOpenView(true)}
        />
        <ReportActionButton
          icon={Download}
          text={t('download')}
          onClick={() => onDownload()}
        />
        <ReportActionButton
          icon={Trash2}
          text={t('delete')}
          onClick={() => onDelete({ reading_id: [readingId] })}
          isLoading={isDeleting}
          className='text-red-500 border-red-500 hover:bg-red-500/10 hover:text-red-500'
        />
      </div>

      <ViewReportDialog
        open={openView}
        onClose={() => setOpenView(false)}
        report={scanReport}
      />
    </>
  );
}
